import { useEffect, useState } from "react";
import { T } from "@/components/T";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fetchMyBookings, myRecommendations, shareBirthConsent } from "@/lib/api";
import { Sparkles, Share2, Check } from "lucide-react";

const STATUS_STYLE = {
  new: "text-gold border-[var(--gold)]",
  contacted: "text-saffron border-saffron",
  confirmed: "text-[#2f7d32] border-[#2f7d32]",
  assigned: "text-saffron border-saffron",
  advised: "text-[#2f7d32] border-[#2f7d32]",
  completed: "text-ink-2 border-line",
  cancelled: "text-[var(--saffron-dark)] border-[var(--saffron-dark)]",
};

function Recommendation({ rec }) {
  return (
    <div className="mt-3 border-t border-line pt-3" data-testid="booking-recommendation">
      <p className="flex items-center gap-2 text-sm font-medium text-ink">
        <Sparkles className="h-4 w-4 text-saffron" />{rec.title || <T>Recommendation</T>}
        {rec.professional_name && <span className="text-xs font-normal text-ink-3">· {rec.professional_name}</span>}
      </p>
      {rec.summary && <p className="mt-1 text-sm text-ink-2">{rec.summary}</p>}
      {rec.remedies?.length > 0 && (
        <div className="mt-2">
          <p className="text-xs uppercase tracking-wider text-ink-3"><T>Remedies</T></p>
          <ul className="mt-1 list-disc pl-5 text-sm text-ink-2">{rec.remedies.map((r, i) => <li key={i}>{r}</li>)}</ul>
        </div>
      )}
      {rec.mantras?.length > 0 && (
        <div className="mt-2">
          <p className="text-xs uppercase tracking-wider text-ink-3"><T>Mantras</T></p>
          <ul className="mt-1 list-disc pl-5 text-sm text-ink-2">{rec.mantras.map((m, i) => <li key={i}>{m}</li>)}</ul>
        </div>
      )}
      {rec.follow_up && <p className="mt-2 text-sm text-ink-3"><T>Follow-up:</T> {rec.follow_up}</p>}
    </div>
  );
}

export default function MyBookings() {
  const { user, ready } = useAuth();
  const [items, setItems] = useState([]);
  const [recs, setRecs] = useState({});
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState("");

  useEffect(() => {
    if (!user) return;
    Promise.all([fetchMyBookings(), myRecommendations().catch(() => ({ recommendations: [] }))])
      .then(([bks, r]) => {
        setItems(bks);
        const map = {};
        (r.recommendations || []).forEach((x) => { map[x.booking_id] = x; });
        setRecs(map);
      })
      .finally(() => setLoading(false));
  }, [user]);

  const share = async (b) => {
    setSharing(b.booking_id);
    try {
      await shareBirthConsent(b.booking_id, true);
      toast.success("Your birth chart is now shared with your professional");
      setItems((prev) => prev.map((x) => x.booking_id === b.booking_id ? { ...x, share_birth_consent: true } : x));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not share"); }
    setSharing("");
  };

  if (ready && !user) return <Navigate to="/login" replace />;

  return (
    <div className="mx-auto max-w-4xl px-5 py-16 md:px-8 md:py-20">
      <h1 className="font-serif-latin text-3xl text-ink"><T>My Bookings</T></h1>
      <p className="mt-1 text-sm text-ink-3"><T>Your pooja, yagya and consultation requests.</T></p>

      {loading ? (
        <p className="py-16 text-center text-ink-3">…</p>
      ) : items.length === 0 ? (
        <div className="mt-10 border border-line bg-surface py-16 text-center">
          <p className="text-ink-3"><T>You have no bookings yet.</T></p>
          <Link to="/services/pooja" className="mt-4 inline-block text-saffron link-underline"><T>Book a pooja →</T></Link>
        </div>
      ) : (
        <div className="mt-10 space-y-4">
          {items.map((b) => {
            const canShare = b.booking_type === "astrology" && b.professional_id && !b.share_birth_consent;
            return (
              <div key={b.booking_id} className="border border-line bg-surface p-5" data-testid={`my-booking-${b.booking_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-serif-latin text-lg text-ink">{b.service_name || b.booking_type}</p>
                    <p className="text-sm text-ink-3 capitalize">{b.booking_type} · {b.preferred_date || b.birth_date || "date to confirm"}</p>
                    {b.professional_name && <p className="mt-0.5 text-xs text-ink-3"><T>With</T> {b.professional_name}</p>}
                    <p className="mt-1 font-mono text-xs text-ink-3">{b.booking_id}</p>
                  </div>
                  <span className={`shrink-0 border px-3 py-1 text-xs uppercase tracking-wider ${STATUS_STYLE[b.status] || "text-ink-2 border-line"}`}>{b.status}</span>
                </div>

                {canShare && (
                  <button onClick={() => share(b)} disabled={sharing === b.booking_id} data-testid={`share-birth-${b.booking_id}`}
                    className="mt-3 inline-flex items-center gap-2 border border-saffron px-3 py-1.5 text-xs text-saffron hover:bg-saffron/5 disabled:opacity-50">
                    <Share2 className="h-3.5 w-3.5" /><T>Share my birth chart with my professional</T>
                  </button>
                )}
                {b.booking_type === "astrology" && b.share_birth_consent && (
                  <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-green-700" data-testid={`shared-birth-${b.booking_id}`}>
                    <Check className="h-3.5 w-3.5" /><T>Birth chart shared with your professional</T>
                  </p>
                )}

                {recs[b.booking_id] && <Recommendation rec={recs[b.booking_id]} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
